import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  BulkUpdateDocumentDto,
  CreateDocumentDto,
  DocumentDto,
  DocumentListDto,
  DocumentUploadStage,
  GenerateUploadUrlResponseDto,
  PostprocessUploadedDocumentDto,
  SelectListDto,
  UpdateDocumentDto,
  UploadDocumentDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import {
  InferSearchParamsQueryTypeWithArraySorter,
  PaginationOptionsWithSortArray,
} from '@sib/api/types';

export type DocumentsKeys = keyof DocumentDto;

export const DOCUMENT_RESOURCE_NAME = 'document';

export const DocumentResourceField = {
  ID: 'id',
  STAGE: 'stage',
  VENDOR: 'vendor',
  FILE_URL: 'fileUrl',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  NAME: 'name',
  PROVIDED_BY: 'providedBy',
  SPEND_CATEGORY: 'spendCategory',
} as const satisfies Record<string, DocumentsKeys>;

const CACHE_TIME = 5000;

export interface DocumentsListQuery extends PaginationOptionsWithSortArray {
  searchTerm?: string;
  afterEndDateOrNull?: string;
  originFilename?: string;
}

export const useDocumentsList = (
  organizationId: string,
  query: DocumentsListQuery,
  options?: UseQueryOptions<DocumentListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.Documents,
      query,
      organizationId,
      ...(options?.queryKey || []),
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetAllDocuments(
        organizationId || '',
        query
      ),
  });

export const useDocument = (
  documentId?: string,
  organizationId?: string,
  options?: UseQueryOptions<DocumentDto>
) =>
  useQuery({
    ...options,
    enabled: !!documentId && !!organizationId,
    queryKey: [QueryKey.Resource.Documents, documentId, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetOneDocument(
        documentId || '',
        organizationId || ''
      ),
  });

export type UseUploadDocumentOptions = UseMutationOptions<
  DocumentDto,
  Error,
  UploadDocumentDto
> & {
  organizationId: string;
};

export const useUploadDocument = ({
  organizationId,
  ...options
}: UseUploadDocumentOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Documents],
      });
      options?.onSuccess?.(...args);
    },
    mutationFn: async data =>
      api.organization.documentControllerUploadFile(organizationId, data),
  });
};

export interface DeleteDocumentPayload {
  organizationId: string;
  documentId: string;
}

export const useDocumentDelete = (
  options?: UseMutationOptions<unknown, Error, DeleteDocumentPayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Documents],
      });
      options?.onSuccess?.(...args);
    },
    mutationFn: async ({ documentId, organizationId }) =>
      api.organization.documentControllerDeleteDocuement(
        documentId,
        organizationId
      ),
  });
};

export type UseUpdatedDocumentOptions = UseMutationOptions<
  DocumentDto,
  Error,
  UpdateDocumentDto
> & {
  organizationId: string;
  documentId: string;
  updateDataOnSuccess?: boolean;
};

export const useUpdateDocument = ({
  organizationId,
  documentId,
  updateDataOnSuccess = false,
  ...options
}: UseUpdatedDocumentOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      api.organization.documentControllerUpdate(
        documentId,
        organizationId,
        data
      ),
    onSuccess: async (documentData, variables) => {
      if (variables.vendor !== undefined) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.DocumentVendorSelectList],
        });
      }
      if (!updateDataOnSuccess) return;

      await queryClient.cancelQueries({
        queryKey: [QueryKey.Resource.Documents],
      });
      const previousDocuments = queryClient.getQueryData<DocumentListDto>([
        QueryKey.Resource.Documents,
      ]);

      queryClient.setQueriesData<DocumentListDto>(
        { queryKey: [QueryKey.Resource.Documents] },
        old => {
          if (!old) return undefined;

          return {
            ...old,
            items: old.items.map(item => {
              if (item.id === documentData.id) {
                return { ...item, ...documentData };
              }

              return item;
            }),
          };
        }
      );

      return { previousDocuments };
    },
  });
};

interface UseCreateDocumentUploadUrlProps {
  organizationId: string;
}

export const useCreateDocumentUploadUrl = (
  { organizationId }: UseCreateDocumentUploadUrlProps,
  options?: UseMutationOptions<
    GenerateUploadUrlResponseDto,
    Error,
    CreateDocumentDto
  >
): UseMutationResult<GenerateUploadUrlResponseDto, Error, CreateDocumentDto> =>
  useMutation({
    ...options,
    mutationFn: (document: CreateDocumentDto) =>
      api.organization.documentControllerGetUploadUrl(organizationId, document),
  });

interface UsePostProcessDocumentsProps {
  organizationId: string;
}

export const usePostprocessDocument = (
  { organizationId }: UsePostProcessDocumentsProps,
  options?: UseMutationOptions<void, Error, PostprocessUploadedDocumentDto>
): UseMutationResult<void, Error, PostprocessUploadedDocumentDto> =>
  useMutation({
    ...options,
    mutationFn: body =>
      api.organization.documentControllerPostprocessUploadedFile(
        organizationId,
        body
      ),
  });

interface UseGetDocumentUploadStatusParams {
  batchId: string;
  organizationId: string;
}

export const useGetDocumentUploadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: data => {
      const areAllFilesProcessed =
        data.items.length > 0 &&
        data.items.every(item =>
          [DocumentUploadStage.Failed, DocumentUploadStage.Uploaded].includes(
            item.status
          )
        );

      if (areAllFilesProcessed) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Documents],
        });
      }
    },
    mutationFn: ({
      organizationId,
      batchId,
    }: UseGetDocumentUploadStatusParams) =>
      api.organization.documentControllerGetUploadStatus(
        organizationId,
        batchId
      ),
  });
};

export const useDocumentTypeSelectList = (
  organizationId: string,
  options?: UseQueryOptions<SelectListDto> & {
    filter?: string[];
    sort?: string[];
  }
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.DocumentTypeSelectList,
      organizationId,
      options?.filter,
      options?.sort,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetDocumentTypeSelectList(
        organizationId || '',
        {
          filter: options?.filter,
          sort: options?.sort,
        }
      ),
  });

export const useDocumentSpendCategorySelectList = (
  organizationId: string,
  query: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.organization.documentControllerGetSpendCategorySelectList,
    1
  >,
  options?: UseQueryOptions<SelectListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.DocumentSpendCategorySelectList,
      organizationId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetSpendCategorySelectList(
        organizationId || '',
        query
      ),
  });

export const useDocumentVendorSelectList = (
  organizationId: string,
  options?: UseQueryOptions<SelectListDto> & {
    filter?: string[];
    sort?: string[];
  }
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.DocumentVendorSelectList,
      organizationId,
      options?.filter,
      options?.sort,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetVendorSelectList(
        organizationId || '',
        {
          filter: options?.filter,
          sort: options?.sort,
        }
      ),
  });

export type UseBulkUpdateDocumentsOptions = UseMutationOptions<
  DocumentDto[],
  Error,
  BulkUpdateDocumentDto
> & {
  organizationId: string;
};

export const useBulkUpdateDocuments = ({
  organizationId,
  ...options
}: UseBulkUpdateDocumentsOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (data: BulkUpdateDocumentDto) =>
      api.organization.documentControllerBulkUpdate(organizationId, data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Documents],
      });

      if (variables.vendor !== undefined) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.DocumentVendorSelectList],
        });
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
};
